'use client';
import React, { FC, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { CatButton, CatSkeleton } from 'components/blocks-components';
import { Conditional, Portal } from '@cat-ecom/pcc-components';
import { useProductDetailPageContext } from '../../../context/product-detail';
import { subTotalResponse } from 'services/subTotal/types';
import { CurrentItems, FbtProductWithPrice } from 'services/fbtItems/types';
import { getFBTSubTotal, addAllToCart } from 'services/fbtItems/actions';
import {
  checkIsNotWON,
  checkIsRiyal,
  getRiyalFormattedPrice,
  removeDecimalsForWonCurrency
} from 'app/[lang]/[esite]/utils/paymentUtils';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import {
  getFormattedItemData,
  getFormattedSearchSelectItemHeadless
} from 'components/product-comparison-grid/product-comparison-card/utils';
import {
  DEBOUNCE_TIME_SECONDS,
  FBT_LIST_NAME
} from '../../../constants/constants';
import { useQuickToast } from 'hooks/use-toast';
import cx from 'classnames';
import styles from './fbt-subtotal-action.module.scss';
import { useFbtMobileDrawer } from '../fbt-mobile-drawer/fbt-mobile-drawer-context';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { useFbtContext } from '../fbt-context/fbt-context-provider';
import { getItemByPartNumber } from '../utils';
import { useCoreContext } from 'providers/core-provider';

interface FbtSubtotalActionProps {
  partsPriceData: FbtProductWithPrice[];
  currentProduct: CurrentItems;
  currency: string;
  subTotal: subTotalResponse;
  isFrequentlyBoughtMobile: boolean;
  isCatCorp: boolean;
  storeLocationId: string;
  isRTL: boolean;
  currencyLdFeatureFlag?: boolean;
  contactDealerComp: React.ReactNode;
}

const FbtSubtotalAction: FC<FbtSubtotalActionProps> = ({
  partsPriceData,
  subTotal: subTotalInitial,
  currency,
  currentProduct,
  isFrequentlyBoughtMobile,
  isCatCorp = false,
  storeLocationId,
  isRTL = false,
  currencyLdFeatureFlag = false,
  contactDealerComp
}) => {
  const t = useTranslations();
  const { closeDrawer } = useFbtMobileDrawer();
  const toast = useQuickToast();
  const { openDrawer } = useDealerLocatorDrawer();
  const { quantity } = useProductDetailPageContext();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const isRiyal = currencyLdFeatureFlag && checkIsRiyal(currency);
  const {
    subTotal: subTotalContext,
    isFbtSubTotalLoading,
    setIsFbtSubTotalLoading,
    setSubTotal,
    cartDetails,
    itemSummary,
    fbtProductsWithPrice
  } = useFbtContext();
  const { broadcastCartUpdate } = useCoreContext();

  const cartId = cartDetails?.orderHeader?.orderId;

  const subTotal = subTotalContext || subTotalInitial;

  useEffect(() => {
    (async () => {
      try {
        setIsFbtSubTotalLoading(true);
        const displayPartsPriceData =
          fbtProductsWithPrice?.length > 0
            ? fbtProductsWithPrice
            : partsPriceData;
        const partsQtyPriceData = displayPartsPriceData?.map((item) => {
          return {
            ...item,
            quantity:
              item.partNumber === currentProduct.partNumber
                ? quantity
                : item.quantity
          };
        });

        const { subTotalResponse } = await getFBTSubTotal({
          currency: currency,
          fbtParts: partsQtyPriceData,
          isHeadless: true
        });
        setSubTotal(subTotalResponse);
      } catch (error) {
        setIsFbtSubTotalLoading(false);
      } finally {
        setIsFbtSubTotalLoading(false);
      }
    })();
  }, [
    quantity,
    currency,
    currentProduct.partNumber,
    partsPriceData,
    fbtProductsWithPrice,
    setSubTotal,
    setIsFbtSubTotalLoading
  ]);

  const handleAddToCartForMobile = () => {
    handleAddToCart(true);
  };

  const fbtPartsPriceData =
    fbtProductsWithPrice?.length > 0 ? fbtProductsWithPrice : partsPriceData;

  const isTotalPriceSectionVisible = fbtPartsPriceData?.filter(
    (item) => item?.partLevelFlags?.isAddToCartEnabled
  )?.length;

  const isTotalPriceEnabled = fbtPartsPriceData?.filter(
    (item) => item.isChecked
  ).length;

  const priceDisplayFormat = (price, currency = '', spanClassFbt = false) => {
    const isNotWon = checkIsNotWON(price, currency);
    const showPrice = () => {
      return isRiyal ? (
        getRiyalFormattedPrice({
          price: price,
          isRTL: isRTL,
          priceWrapperClasses: cx('px-1', { [styles['price']]: spanClassFbt })
        })
      ) : (
        <div className="self-align-bottom">
          <span className={cx({ [styles['price']]: spanClassFbt })}>
            {price}
          </span>
          <span
            className={cx({ 'cat-u-theme-typography-label ': spanClassFbt })}
          >
            {currency}
          </span>
        </div>
      );
    };
    return (
      <>
        {isNotWon ? (
          showPrice()
        ) : (
          <div>{`${removeDecimalsForWonCurrency(price)}`}</div>
        )}
      </>
    );
  };

  const handleAddToCart = async (isFbtMobile = false) => {
    const partsQtyPriceData = fbtPartsPriceData?.map((item) => {
      return {
        ...item,
        quantity:
          item.partNumber === currentProduct.partNumber
            ? quantity
            : item.quantity
      };
    });
    await addAllToCart(partsQtyPriceData);
    toast.success({ message: t('ITEM_ADDED_TO_CART') });
    broadcastCartUpdate();

    const selectedItems = partsPriceData?.filter((item) => item?.isChecked);

    const partPrice = getItemByPartNumber(
      currentProduct.partNumber,
      partsPriceData
    );

    fireSelectItemEvent({
      item: getFormattedSearchSelectItemHeadless(
        { orderItemId: cartId },
        partPrice
      ),
      locationId: storeLocationId,
      partName: '',
      itemSummary: selectedItems.map((item) => {
        const productData = itemSummary?.products?.find(
          (p) => p.partNumber === item.partNumber
        );
        return {
          ...item,
          categoryPath: item?.categoryPath,
          manufacturer: productData?.manufacturer ?? 'Cat',
          name: productData?.name,
          index: itemSummary?.products?.findIndex(
            (p) => p?.partNumber === item?.partNumber
          )
        };
      }),
      itemListName: FBT_LIST_NAME,
      itemListId: FBT_LIST_NAME,
      serialNumber: '',
      isSearchPageEvent: true,
      fitment: ''
    });
    if (isFbtMobile) {
      setTimeout(() => {
        closeDrawer();
      }, DEBOUNCE_TIME_SECONDS);
    }
    addToCartItemEvent({
      itemSummary: selectedItems.map((item) => ({
        ...item,
        manufacturer:
          itemSummary?.products?.find((p) => p.partNumber === item.partNumber)
            .manufacturer ?? 'Cat',
        index: itemSummary.products?.findIndex(
          (p) => p?.partNumber === item?.partNumber
        )
      })),
      locationId: storeLocationId,
      itemData: selectedItems.flatMap((item) =>
        getFormattedItemData(cartId, item, currency)
      ),
      itemListName: FBT_LIST_NAME,
      itemListId: FBT_LIST_NAME
    });
  };
  return (
    <>
      <>
        {!!isTotalPriceSectionVisible && (
          <div
            className={cx(
              'd-sm-flex justify-content-md-end justify-content-center py-2',
              { 'd-none d-md-block': isFrequentlyBoughtMobile }
            )}
          >
            {subTotal.unformattedSubtotal > 0 && (
              <Conditional
                test={!isFbtSubTotalLoading}
                fallback={
                  <CatSkeleton
                    size="lg"
                    data-testid="loader-id"
                    className={`d-flex justify-content-center align-self-center ${styles['subtotal-loader']}`}
                  />
                }
              >
                <div className="d-flex cat-u-theme-typography-title-sm pe-3 pb-2 justify-content-center">
                  <div className="pe-1 align-self-center">
                    {`${t('TOTAL_PRICE')}:`}
                  </div>
                  {isRiyal
                    ? getRiyalFormattedPrice({
                        priceWrapperClasses: 'px-1',
                        price: subTotal?.formattedSubtotal,
                        isRTL: isRTL,
                        size: 'sm'
                      })
                    : priceDisplayFormat(subTotal?.formattedSubtotal, currency)}
                </div>
              </Conditional>
            )}

            <div>
              <CatButton
                className="text-capitalize"
                size="sm"
                fullWidth
                variant="primary"
                disabled={!isTotalPriceEnabled}
                onClick={() => handleAddToCart()}
              >
                {t('PM_ADD_ALL_TO_CART')}
              </CatButton>
            </div>
          </div>
        )}
      </>
      <div className={cx({ 'd-md-none': isFrequentlyBoughtMobile })}>
        <Portal id="fbt-mobile-drawer-footer-container">
          <Conditional test={isCatCorp}>
            <CatButton
              size="md"
              variant="contrast"
              fullWidth
              onClick={openDrawer}
            >
              {t('CAT_CHECK_PRICE')}
            </CatButton>
          </Conditional>
          <Conditional
            test={!!isTotalPriceSectionVisible}
            fallback={!isCatCorp && contactDealerComp}
          >
            <div
              className={`d-sm-flex flex-column justify-content-md-end justify-content-center py-2`}
            >
              <Conditional test={!!isTotalPriceEnabled}>
                <div className="d-flex cat-u-theme-typography-title-sm justify-content-between pb-2">
                  <div className="align-self-center pe-1">{`${t('TOTAL_PRICE')}:`}</div>
                  {isRiyal
                    ? getRiyalFormattedPrice({
                        isRTL: isRTL,
                        price: subTotal?.formattedSubtotal,
                        priceWrapperClasses: 'px-1',
                        size: 'sm'
                      })
                    : priceDisplayFormat(
                        subTotal?.formattedSubtotal,
                        currency,
                        true
                      )}
                </div>
              </Conditional>
              <div>
                <CatButton
                  size="md"
                  disabled={!isTotalPriceEnabled}
                  className="text-capitalize"
                  fullWidth
                  variant="primary"
                  onClick={handleAddToCartForMobile}
                >
                  {t('PM_ADD_ALL_TO_CART')}
                </CatButton>
              </div>
            </div>
          </Conditional>
        </Portal>
      </div>
    </>
  );
};

export default FbtSubtotalAction;
